import PropTypes from 'prop-types';
import Loading from 'Components/Atoms/Loading/Loading';
import { useTranslation } from 'react-i18next';
import useRelatedInvoices from 'Hooks/useRelatedInvoices';
import { DRAWER_STACK } from 'Utils/constants';
import useDrawerStack from 'Hooks/useDrawerStack';
import InvoiceTitle from 'Components/Atoms/InvoiceTitle/InvoiceTitle';
import InvoiceQuickEditorDrawerRelatedItem from './InvoiceQuickEditorDrawerRelatedItem';
import { formatCurrency } from 'Utils/formatters';
import useCurrency from 'Hooks/useCurrency';
import useLocale from 'Hooks/useLocale';

export default function InvoiceQuickEditorDrawerRelated(props) {
    const { id } = props;

    const { t } = useTranslation('invoices');
    const { importedInvoices, issuedInvoices, correctedInvoices, correctiveInvoices, isLoading } =
        useRelatedInvoices(id);
    const { open } = useDrawerStack();
    const currency = useCurrency();
    const locale = useLocale();

    const handleOpenInvoice = (invoice) => {
        open(DRAWER_STACK.INVOICE_QUICK_EDITOR, {
            invoiceId: invoice.id,
            title: <InvoiceTitle invoice={invoice} />,
        });
    };

    const hasInvoices = [importedInvoices, issuedInvoices, correctedInvoices, correctiveInvoices].some(
        (invoices) => invoices.length > 0,
    );

    const totalImportedAmount = importedInvoices.reduce((sum, invoice) => sum + (invoice.amount_uc || 0), 0);
    const totalIssuedAmount = issuedInvoices.reduce((sum, invoice) => sum + (invoice.amount_uc || 0), 0);
    const totalCorrectedAmount = correctedInvoices.reduce((sum, invoice) => sum + (invoice.amount_uc || 0), 0);
    const totalCorrectiveAmount = correctiveInvoices.reduce((sum, invoice) => sum + (invoice.amount_uc || 0), 0);

    return (
        <div>
            {isLoading && (
                <div className="text-center py-6">
                    <Loading />
                </div>
            )}

            {!isLoading && !hasInvoices && (
                <div className="text-center text-dark-blue/50 py-6 text-sm">{t('no-related-invoices')}</div>
            )}

            {!isLoading && hasInvoices && (
                <div className="grid gap-5">
                    {importedInvoices.length > 0 && (
                        <div>
                            <div className="text-gray-500 py-2 text-2xs uppercase tracking-wider font-semibold">
                                {t('imported-invoices')} - {formatCurrency(totalImportedAmount, { currency, locale })}
                            </div>
                            <div className="grid gap-3">
                                {importedInvoices.map((invoice) => (
                                    <InvoiceQuickEditorDrawerRelatedItem
                                        key={invoice.id}
                                        invoice={invoice}
                                        onClick={() => handleOpenInvoice(invoice)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {issuedInvoices.length > 0 && (
                        <div>
                            <div className="text-gray-500 py-2 text-2xs uppercase tracking-wider font-semibold">
                                {t('issued-invoices')} - {formatCurrency(totalIssuedAmount, { currency, locale })}
                            </div>
                            <div className="grid gap-3">
                                {issuedInvoices.map((invoice) => (
                                    <InvoiceQuickEditorDrawerRelatedItem
                                        key={invoice.id}
                                        invoice={invoice}
                                        onClick={() => handleOpenInvoice(invoice)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {correctedInvoices.length > 0 && (
                        <div>
                            <div className="text-gray-500 py-2 text-2xs uppercase tracking-wider font-semibold">
                                {t('corrected-invoices')} - {formatCurrency(totalCorrectedAmount, { currency, locale })}
                            </div>
                            <div className="grid gap-3">
                                {correctedInvoices.map((invoice) => (
                                    <InvoiceQuickEditorDrawerRelatedItem
                                        key={invoice.id}
                                        invoice={invoice}
                                        onClick={() => handleOpenInvoice(invoice)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {correctiveInvoices.length > 0 && (
                        <div>
                            <div className="text-gray-500 py-2 text-2xs uppercase tracking-wider font-semibold">
                                {t('corrective-invoices')} -{' '}
                                {formatCurrency(totalCorrectiveAmount, { currency, locale })}
                            </div>
                            <div className="grid gap-3">
                                {correctiveInvoices.map((invoice) => (
                                    <InvoiceQuickEditorDrawerRelatedItem
                                        key={invoice.id}
                                        invoice={invoice}
                                        onClick={() => handleOpenInvoice(invoice)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

InvoiceQuickEditorDrawerRelated.propTypes = {
    id: PropTypes.string.isRequired,
};
